import React from 'react';
import {
  Skeleton, SkeletonButton, SkeletonChart, SkeletonForm, SkeletonRegion, SkeletonStats, SkeletonTable,
} from '@parsina/framework';
import { C, RADIUS } from '@parsina/framework/tokens';

import { PANEL_PAD, STACK } from '../../constants/density';
import { useTranslation } from '../../i18n';
import useResponsive from '../../hooks/useResponsive';
import { MobileScreen, MobileHeader, MobileCardSkeleton } from '../../mobile';
import {
  MobileSectionStrip, RailSkeleton, TableSurface,
  WORKBENCH_HEADER, WORKBENCH_MAIN, WORKBENCH_ROOT,
} from '../../components/common/WorkbenchSkeleton';

/**
 * «فروشگاه» while it is still fetching — the page's own shape, per view
 * (design §14.3; DESIGN.md §"Loading & skeleton").
 *
 * The router's Suspense fallback imports this EAGERLY and the screen uses the
 * same component for its first load, so nothing jumps between the two. That is
 * why it imports nothing from the screen: only the kit, the tokens, density,
 * i18n, the mobile primitives and the shared workbench geometry.
 *
 * Desktop: header band · five KPI blocks · rail (3 + 3) · the view's surface.
 * Mobile:  the real header · 4 tile placeholders · 6 card placeholders.
 */

const TABLE_VIEWS = {
  listings: { columns: [40, 220, 84, 120, 130, 120, 150, 110, 120], rows: 8, filters: { search: 220, select: 170, chips: 4 } },
  orders: { columns: [110, 180, 180, 120, 120, 130, 120, 150], rows: 8, filters: { search: 220, select: 0, chips: 5 } },
};

export const STORE_SKELETON_VIEWS = ['dashboard', 'orders', 'listings', 'storefront', 'finance', 'settings'];

export function resolveStoreSkeletonView(view) {
  return STORE_SKELETON_VIEWS.includes(view) ? view : 'dashboard';
}

function Surface({ view, label }) {
  if (TABLE_VIEWS[view]) return <TableSurface spec={TABLE_VIEWS[view]} label={label} />;
  if (view === 'dashboard') {
    return (
      <div style={{ flex: 1, overflow: 'hidden', padding: PANEL_PAD.regular, display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 300px', gap: STACK.regular }} data-surface="dashboard">
        <div style={{ display: 'grid', gap: STACK.regular, alignContent: 'start' }}>
          <SkeletonTable dense rows={6} columns={[110, 160, 180, 120, 130, 150]} label={label} />
          <SkeletonChart height={160} label={label} />
        </div>
        <div style={{ display: 'grid', gap: STACK.regular, alignContent: 'start' }} aria-hidden>
          {[120, 150, 140].map((h, i) => <Skeleton key={i} height={h} radius={RADIUS.card} />)}
        </div>
      </div>
    );
  }
  if (view === 'finance') {
    return (
      <div style={{ flex: 1, overflow: 'hidden' }} data-surface="finance">
        <div aria-hidden style={{ display: 'flex', gap: 8, padding: '10px 20px', borderBottom: `1px solid ${C.border}` }}>
          <Skeleton width={36} height={36} radius={RADIUS.control} />
          <Skeleton width={120} height={36} />
          <Skeleton width={36} height={36} radius={RADIUS.control} />
        </div>
        <div style={{ padding: '14px 20px' }}><SkeletonStats count={3} icon={false} label={label} /></div>
        <div style={{ padding: '0 20px' }}><SkeletonTable dense rows={4} columns={6} label={label} /></div>
      </div>
    );
  }
  if (view === 'storefront') {
    return (
      <div style={{ flex: 1, overflow: 'hidden', padding: PANEL_PAD.regular, display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 320px', gap: STACK.regular }} aria-hidden data-surface="storefront">
        <Skeleton height={600} radius={RADIUS.card} />
        <div style={{ display: 'grid', gap: STACK.regular, alignContent: 'start' }}>
          <Skeleton height={300} radius={RADIUS.card} />
          <Skeleton height={140} radius={RADIUS.card} />
        </div>
      </div>
    );
  }
  return (
    <div style={{ flex: 1, overflow: 'hidden' }} data-surface="settings">
      <div aria-hidden style={{ display: 'flex', gap: 4, padding: '10px 20px', borderBottom: `1px solid ${C.border}` }}>
        {[72, 96, 72, 96, 84].map((w, i) => <Skeleton key={i} width={w} height={36} radius={9} />)}
      </div>
      <div style={{ padding: PANEL_PAD.regular, maxWidth: 820 }}><SkeletonForm fields={5} label={label} /></div>
    </div>
  );
}

export function StoreSkeletonWeb({ view: requested = 'dashboard' }) {
  const { t } = useTranslation();
  const view = resolveStoreSkeletonView(requested);
  const label = t('common.state.loading');
  return (
    <SkeletonRegion label={label} data-testid="store-skeleton" data-view={view} style={WORKBENCH_ROOT}>
      <header style={WORKBENCH_HEADER}>
        <div>
          <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: C.textPrimary }}>{t('nav.items.store')}</h1>
          <div aria-hidden style={{ marginTop: 5 }}><Skeleton width={180} height={12} /></div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <SkeletonButton width={120} />
          <SkeletonButton width={110} />
        </div>
      </header>
      <div style={{ padding: '14px 20px', flexShrink: 0 }}>
        <SkeletonStats count={5} icon={false} label={label} />
      </div>
      <div style={{ display: 'flex', flex: 1, minHeight: 0, borderTop: `1px solid ${C.border}` }}>
        <RailSkeleton groups={[3, 3]} />
        <main style={WORKBENCH_MAIN}>
          <Surface view={view} label={label} />
        </main>
      </div>
    </SkeletonRegion>
  );
}

/** One card placeholder per mobile list, shaped like the card it stands in for. */
export const MOBILE_CARD_SKELETONS = {
  listings: () => <MobileCardSkeleton badge={72} metaLines={2} />,
  orders: () => <MobileCardSkeleton badge={64} metaLines={2} chips={2} />,
  finance: () => <MobileCardSkeleton badge={0} metaLines={2} />,
};

export const renderStoreCardSkeleton = (view) => (i) => (
  <React.Fragment key={i}>{(MOBILE_CARD_SKELETONS[view] || MOBILE_CARD_SKELETONS.orders)()}</React.Fragment>
);

export function StoreSkeletonMobile({ view: requested = 'dashboard' }) {
  const { t } = useTranslation();
  const view = resolveStoreSkeletonView(requested);
  const label = t('common.state.loading');
  const card = MOBILE_CARD_SKELETONS[view] || MOBILE_CARD_SKELETONS.orders;
  return (
    <SkeletonRegion label={label} data-testid="store-skeleton" data-view={view} style={{ height: '100%' }}>
      <MobileScreen header={<MobileHeader title={t('nav.items.store')} sticky />}>
        <MobileSectionStrip widths={[56, 48, 52, 48, 44, 56]} />
        <div aria-hidden style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '0.5rem', padding: '0.75rem 1rem 0' }}>
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} height={60} radius={14} style={{ border: `1px dashed ${C.border}` }} />
          ))}
        </div>
        <div style={{ padding: '0.75rem 1rem 1.5rem', display: 'grid', gap: `${STACK.tight}px` }}>
          {Array.from({ length: 6 }, (_, i) => <React.Fragment key={i}>{card()}</React.Fragment>)}
        </div>
      </MobileScreen>
    </SkeletonRegion>
  );
}

export default function StoreSkeleton(props) {
  const { isMobile } = useResponsive();
  return isMobile ? <StoreSkeletonMobile {...props} /> : <StoreSkeletonWeb {...props} />;
}
